import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import ThemeColorsCard from '../components/ThemeColorsCard';
import { 
  Image, Plus, Edit, Trash2, X, Save, Eye, EyeOff, 
  GripVertical, Layout, Layers, AlertCircle, Check,
  Upload, Clock, Link, Package, FolderOpen, Calendar,
  ArrowUp, ArrowDown, RefreshCw, ExternalLink, Video, Flame
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente de Upload de Imagem
const ImageUploader = ({ value, onChange, folder = "banners", label = "Imagem" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setPreview(value || '');
  }, [value]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image/${folder}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fullUrl = response.data.url;
      setPreview(fullUrl);
      onChange(fullUrl);
      toast.success('Imagem enviada!');
    } catch (error) {
      toast.error('Erro ao enviar imagem');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      
      {preview ? (
        <div className="relative group">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-40 object-cover rounded-lg border border-border"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Erro'; }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="p-2 bg-white rounded-full hover:bg-gray-100"
            >
              <Upload className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div 
          onClick={() => inputRef.current?.click()}
          className="w-full h-40 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
        >
          {uploading ? (
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Clique para enviar</p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WebP (máx. 5MB)</p>
            </>
          )}
        </div>
      )}
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

// Componente de Upload Múltiplo de Imagens
const MultiImageUploader = ({ values = [], onChange, folder = "products", max = 9, label = "Fotos", onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remaining = max - values.length;
    if (files.length > remaining) {
      toast.error(`Você pode adicionar no máximo mais ${remaining} imagens`);
      return;
    }

    const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
    const ignored = files.length - validFiles.length;
    if (ignored > 0) toast.warning(`${ignored} arquivo(s) ignorado(s) (tipo inválido ou > 5MB)`);
    if (validFiles.length === 0) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: validFiles.length });

    const newUrls = [...values];
    const uploadedThisRound = []; // URLs adicionadas neste upload (para callback)
    // Upload paralelo em lotes de 4 para acelerar 20-30 imagens
    const BATCH = 4;
    let doneCount = 0;
    for (let i = 0; i < validFiles.length; i += BATCH) {
      const batch = validFiles.slice(i, i + BATCH);
      const results = await Promise.all(batch.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const response = await axios.post(`${API}/upload/image/${folder}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          return response.data?.url || null;
        } catch (error) {
          console.error('Erro ao enviar:', file.name, error);
          return null;
        }
      }));
      results.forEach(u => { if (u) { newUrls.push(u); uploadedThisRound.push(u); } });
      doneCount += batch.length;
      setUploadProgress({ done: doneCount, total: validFiles.length });
    }

    onChange(newUrls);
    setUploading(false);
    setUploadProgress(null);
    const enviados = newUrls.length - values.length;
    if (enviados > 0) toast.success(`${enviados} imagem(ns) enviada(s)!`);
    if (inputRef.current) inputRef.current.value = '';
    // Notifica o pai sobre quais URLs foram adicionadas (para detecção de cor, etc.)
    if (typeof onUploaded === 'function' && uploadedThisRound.length > 0) {
      onUploaded(uploadedThisRound);
    }
  };

  const handleRemove = (index) => {
    const newUrls = values.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  const moveImage = (index, direction) => {
    const newUrls = [...values];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newUrls.length) return;
    [newUrls[index], newUrls[newIndex]] = [newUrls[newIndex], newUrls[index]];
    onChange(newUrls);
  };

  // Drag & drop reorder (desktop). Fallback: setas ↑↓ e swipe/toque no mobile.
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Alguns browsers precisam de data setada
    try { e.dataTransfer.setData('text/plain', String(index)); } catch { /* ignore */ }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex !== null && index !== overIndex) setOverIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setOverIndex(null);
    if (from === null || from === index) return;
    const newUrls = [...values];
    const [moved] = newUrls.splice(from, 1);
    newUrls.splice(index, 0, moved);
    onChange(newUrls);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-col">
          <label className="block text-sm font-medium">{label} ({values.length}/{max})</label>
          {values.length > 1 && (
            <span className="text-[10px] text-muted-foreground italic">
              💡 Arraste as imagens para reordenar (a 1ª é a principal)
            </span>
          )}
        </div>
        {values.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Adicionar
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-3 gap-2">
        {values.map((url, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`relative group aspect-square cursor-grab active:cursor-grabbing transition-all duration-150 ${
              dragIndex === index ? 'opacity-40 scale-95' : ''
            } ${
              overIndex === index && dragIndex !== null && dragIndex !== index
                ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-105'
                : ''
            }`}
            data-testid={`product-image-${index}`}
            title="Arraste para reordenar"
          >
            <img
              src={url}
              alt={`Foto ${index + 1}`}
              draggable={false}
              className="w-full h-full object-cover rounded-lg border border-border pointer-events-none select-none"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => moveImage(index, -1)}
                disabled={index === 0}
                className="p-1 bg-white rounded text-xs disabled:opacity-50"
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => moveImage(index, 1)}
                disabled={index === values.length - 1}
                className="p-1 bg-white rounded text-xs disabled:opacity-50"
              >
                <ArrowDown className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="p-1 bg-red-500 text-white rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            {index === 0 && (
              <span className="absolute top-1 left-1 bg-primary text-white text-xs px-1.5 py-0.5 rounded">
                Principal
              </span>
            )}
          </div>
        ))}
        
        {values.length < max && (
          <div 
            onClick={() => !uploading && inputRef.current?.click()}
            className="aspect-square border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
          >
            {uploading ? (
              <>
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                {uploadProgress && (
                  <span className="text-[10px] text-muted-foreground mt-1">
                    {uploadProgress.done}/{uploadProgress.total}
                  </span>
                )}
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground mt-1">Enviar</span>
              </>
            )}
          </div>
        )}
      </div>
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

// Componente de Upload de Vídeo
const VideoUploader = ({ values = [], onChange, folder = "products", max = 3, label = "Vídeos" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remaining = max - values.length;
    if (files.length > remaining) {
      toast.error(`Você pode adicionar no máximo mais ${remaining} vídeos`);
      return;
    }

    setUploading(true);
    const newUrls = [...values];

    for (const file of files) {
      if (!file.type.startsWith('video/')) continue;
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`Vídeo muito grande: ${file.name}`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/upload/video/${folder}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(response.data.url);
      } catch (error) {
        console.error('Erro ao enviar:', error);
      }
    }

    onChange(newUrls);
    setUploading(false);
    toast.success('Vídeos enviados!');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = (index) => {
    const newUrls = values.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">{label} ({values.length}/{max})</label>
        {values.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Adicionar
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-3 gap-2">
        {values.map((url, index) => (
          <div key={index} className="relative group aspect-video">
            <video 
              src={url} 
              className="w-full h-full object-cover rounded-lg border border-border"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
              <a href={url} target="_blank" rel="noopener noreferrer" className="p-1 bg-white rounded">
                <ExternalLink className="w-3 h-3" />
              </a>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="p-1 bg-red-500 text-white rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
              <Video className="w-3 h-3" />
            </div>
          </div>
        ))}
        
        {values.length < max && (
          <div 
            onClick={() => !uploading && inputRef.current?.click()}
            className="aspect-video border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
          >
            {uploading ? (
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Video className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground mt-1">Enviar</span>
              </>
            )}
          </div>
        )}
      </div>
      
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        multiple
        onChange={handleUpload}
        className="hidden"
      />
      <p className="text-xs text-muted-foreground">MP4, WebM, MOV (máx. 50MB)</p>
    </div>
  );
};

// Modal de Banner
const BannerModal = ({ banner, categories, products, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    description: banner?.description || '',
    image_url: banner?.image_url || '',
    link_type: banner?.link_type || 'none', // none, url, category, product
    link_url: banner?.link_url || '',
    link_category: banner?.link_category || '',
    link_product: banner?.link_product || '',
    button_text: banner?.button_text || '',
    active: banner?.active !== false,
    start_date: banner?.start_date || '',
    end_date: banner?.end_date || '',
    order: banner?.order || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(banner ? 'Banner atualizado!' : 'Banner criado!');
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Image className="w-5 h-5 text-primary" />
            {banner ? 'Editar Banner' : 'Novo Banner'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Upload de Imagem */}
          <ImageUploader
            value={formData.image_url}
            onChange={(url) => setFormData({...formData, image_url: url})}
            folder="banners"
            label="Imagem do Banner (1920x600px recomendado)"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Subtítulo</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={e => setFormData({...formData, subtitle: e.target.value})}
                className="input-field"
                placeholder="Ex: NOVIDADES"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ordem</label>
              <input
                type="number"
                value={formData.order}
                onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                className="input-field"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Título Principal *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="input-field"
              placeholder="Ex: Qualidade que você merece"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="input-field resize-none"
              rows={2}
              placeholder="Texto complementar"
            />
          </div>

          {/* Tipo de Link */}
          <div className="border-t border-border pt-4">
            <label className="block text-sm font-medium mb-2">Link do Banner</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { value: 'none', label: 'Nenhum', icon: X },
                { value: 'url', label: 'URL', icon: Link },
                { value: 'category', label: 'Categoria', icon: FolderOpen },
                { value: 'product', label: 'Produto', icon: Package }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({...formData, link_type: opt.value})}
                  className={`p-2 rounded-lg border text-sm flex flex-col items-center gap-1 transition-colors ${
                    formData.link_type === opt.value 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <opt.icon className="w-4 h-4" />
                  {opt.label}
                </button>
              ))}
            </div>

            {formData.link_type === 'url' && (
              <input
                type="url"
                value={formData.link_url}
                onChange={e => setFormData({...formData, link_url: e.target.value})}
                className="input-field"
                placeholder="https://..."
              />
            )}
            {formData.link_type === 'category' && (
              <select
                value={formData.link_category}
                onChange={e => setFormData({...formData, link_category: e.target.value})}
                className="input-field"
              >
                <option value="">Selecione uma categoria</option>
                {categories.map(cat => (
                  <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            )}
            {formData.link_type === 'product' && (
              <select
                value={formData.link_product}
                onChange={e => setFormData({...formData, link_product: e.target.value})}
                className="input-field"
              >
                <option value="">Selecione um produto</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.codigo} - {p.produto}</option>
                ))}
              </select>
            )}

            {formData.link_type !== 'none' && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">Texto do Botão</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={e => setFormData({...formData, button_text: e.target.value})}
                  className="input-field"
                  placeholder="Ex: CONFIRA"
                />
              </div>
            )}
          </div>

          {/* Timer de Oferta */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-orange-500" />
              <label className="text-sm font-medium">Timer de Oferta (opcional)</label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Data/Hora Início</label>
                <input
                  type="datetime-local"
                  value={formData.start_date ? formData.start_date.slice(0, 16) : ''}
                  onChange={e => setFormData({...formData, start_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Data/Hora Fim</label>
                <input
                  type="datetime-local"
                  value={formData.end_date ? formData.end_date.slice(0, 16) : ''}
                  onChange={e => setFormData({...formData, end_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  className="input-field text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              O banner só será exibido durante este período
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="banner-active"
              checked={formData.active}
              onChange={e => setFormData({...formData, active: e.target.checked})}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="banner-active" className="text-sm">Banner ativo</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Seção
const SectionModal = ({ section, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: section?.title || '',
    subtitle: section?.subtitle || '',
    image_url: section?.image_url || '',
    category: section?.category || '',
    active: section?.active !== false,
    start_date: section?.start_date || '',
    end_date: section?.end_date || '',
    order: section?.order || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(section ? 'Seção atualizada!' : 'Seção criada!');
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            {section ? 'Editar Seção' : 'Nova Seção'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <ImageUploader
            value={formData.image_url}
            onChange={(url) => setFormData({...formData, image_url: url})}
            folder="sections"
            label="Imagem da Seção (600x800px recomendado)"
          />

          <div>
            <label className="block text-sm font-medium mb-1">Título *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="input-field"
              placeholder="Ex: Sacos Ziplock"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subtítulo</label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={e => setFormData({...formData, subtitle: e.target.value})}
              className="input-field"
              placeholder="Ex: Qualidade Premium"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Categoria Vinculada *</label>
            <select
              value={formData.category}
              onChange={e => setFormData({...formData, category: e.target.value})}
              className="input-field"
              required
            >
              <option value="">Selecione</option>
              {categories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Timer */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-orange-500" />
              <label className="text-sm font-medium">Timer (opcional)</label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Início</label>
                <input
                  type="datetime-local"
                  value={formData.start_date ? formData.start_date.slice(0, 16) : ''}
                  onChange={e => setFormData({...formData, start_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Fim</label>
                <input
                  type="datetime-local"
                  value={formData.end_date ? formData.end_date.slice(0, 16) : ''}
                  onChange={e => setFormData({...formData, end_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  className="input-field text-sm"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ordem</label>
              <input
                type="number"
                value={formData.order}
                onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                className="input-field"
                min="0"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({...formData, active: e.target.checked})}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Ativo</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Categoria
const CategoryModal = ({ category, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    image_url: category?.image_url || '',
    active: category?.active !== false,
    order: category?.order || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(category ? 'Categoria atualizada!' : 'Categoria criada!');
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-primary" />
            {category ? 'Editar Categoria' : 'Nova Categoria'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <ImageUploader
            value={formData.image_url}
            onChange={(url) => setFormData({...formData, image_url: url})}
            folder="categories"
            label="Imagem da Categoria"
          />

          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="input-field"
              placeholder="Ex: Saco Ziplock"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="input-field resize-none"
              rows={2}
              placeholder="Descrição da categoria"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ordem</label>
              <input
                type="number"
                value={formData.order}
                onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                className="input-field"
                min="0"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({...formData, active: e.target.checked})}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Ativa</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Página Principal
export const SiteEditor = () => {
  const [banners, setBanners] = useState([]);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [vitrines, setVitrines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('banners');
  const [siteSettings, setSiteSettings] = useState({ top_bar_text: '', whatsapp_number: '', store_address: '', store_maps_url: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [minOrder, setMinOrder] = useState(0);
  const [savingMinOrder, setSavingMinOrder] = useState(false);
  
  // Modais
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showVitrineModal, setShowVitrineModal] = useState(false);
  const [editingVitrine, setEditingVitrine] = useState(null);

  // Drag-and-drop reorder
  const [dragItem, setDragItem] = useState(null); // {kind: 'banner'|'section'|'category', index, id}
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const reorderArray = (arr, from, to) => {
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const persistOrders = async (kind, list) => {
    // Re-atribui order=index e salva em paralelo via PUT
    const endpoint = kind === 'banner' ? 'site-banners' : kind === 'section' ? 'site-sections' : 'categories';
    try {
      await Promise.all(
        list.map((item, idx) => {
          if ((item.order ?? -1) === idx) return Promise.resolve();
          return axios.put(`${API}/${endpoint}/${item.id}`, { ...item, order: idx });
        })
      );
    } catch (e) {
      console.error('reorder save error', e);
      toast.error('Não foi possível salvar a nova ordem');
      fetchData();
    }
  };

  const handleDragStart = (kind, index, id) => (e) => {
    setDragItem({ kind, index, id });
    try { e.dataTransfer.effectAllowed = 'move'; } catch (_) { /* ignore */ }
  };

  const handleDragOver = (kind, index) => (e) => {
    if (!dragItem || dragItem.kind !== kind) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* ignore */ }
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (kind, index) => async (e) => {
    e.preventDefault();
    if (!dragItem || dragItem.kind !== kind) {
      setDragItem(null);
      setDragOverIndex(null);
      return;
    }
    const from = dragItem.index;
    if (from === index) {
      setDragItem(null);
      setDragOverIndex(null);
      return;
    }
    if (kind === 'banner') {
      const next = reorderArray(banners, from, index).map((b, i) => ({ ...b, order: i }));
      setBanners(next);
      await persistOrders('banner', next);
    } else if (kind === 'section') {
      const next = reorderArray(sections, from, index).map((s, i) => ({ ...s, order: i }));
      setSections(next);
      await persistOrders('section', next);
    } else if (kind === 'category') {
      const next = reorderArray(categories, from, index).map((c, i) => ({ ...c, order: i }));
      setCategories(next);
      await persistOrders('category', next);
    }
    setDragItem(null);
    setDragOverIndex(null);
    toast.success('Ordem atualizada');
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverIndex(null);
  };

  const saveMinOrder = async () => {
    setSavingMinOrder(true);
    try {
      await axios.put(`${API}/admin/cliente-min-order`, { valor_minimo: Number(minOrder) || 0 });
      toast.success('Pedido mínimo salvo!');
    } catch (e) {
      toast.error('Erro ao salvar');
    } finally {
      setSavingMinOrder(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [bannersRes, sectionsRes, categoriesRes, productsRes, settingsRes, minRes, vitrinesRes] = await Promise.all([
        axios.get(`${API}/site-banners`),
        axios.get(`${API}/site-sections`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/admin/cliente-min-order`).catch(() => ({ data: { valor_minimo: 0 } })),
        axios.get(`${API}/vitrines`).catch(() => ({ data: [] })),
      ]);
      setBanners(bannersRes.data || []);
      setSections(sectionsRes.data || []);
      setCategories(categoriesRes.data || []);
      setProducts(productsRes.data || []);
      setVitrines(vitrinesRes.data || []);
      const defaultLayout = [
        {id: 'marquee', name: 'Letreiro Superior', enabled: true, order: 0},
        {id: 'banner', name: 'Banner/Carrossel', enabled: true, order: 1},
        {id: 'benefits', name: 'Benefícios', enabled: true, order: 2},
        {id: 'sections', name: 'Seções de Destaque', enabled: true, order: 3},
        {id: 'collections', name: 'Coleções', enabled: true, order: 4},
        {id: 'products', name: 'Produtos', enabled: true, order: 5}
      ];
      setSiteSettings({
        top_bar_text: settingsRes.data?.top_bar_text || '',
        whatsapp_number: settingsRes.data?.whatsapp_number || '',
        store_address: settingsRes.data?.store_address || '',
        store_maps_url: settingsRes.data?.store_maps_url || '',
        store_cnpj: settingsRes.data?.store_cnpj || '',
        store_nome_fantasia: settingsRes.data?.store_nome_fantasia || '',
        store_email: settingsRes.data?.store_email || '',
        marquee_texts: settingsRes.data?.marquee_texts || ['', '', ''],
        marquee_bg_color: settingsRes.data?.marquee_bg_color || '#ea580c',
        marquee_text_color: settingsRes.data?.marquee_text_color || '#ffffff',
        marquee_speed: settingsRes.data?.marquee_speed || 'normal',
        marquee_glow: settingsRes.data?.marquee_glow || false,
        marquee_blink: settingsRes.data?.marquee_blink || false,
        layout_sections: settingsRes.data?.layout_sections || defaultLayout,
        pedido_banner_url: settingsRes.data?.pedido_banner_url || '',
        pedido_banner_rounded: settingsRes.data?.pedido_banner_rounded !== false,
        admin_theme_colors: settingsRes.data?.admin_theme_colors || {},
        vendedor_banner_url: settingsRes.data?.vendedor_banner_url || '',
        vendedor_banner_link: settingsRes.data?.vendedor_banner_link || ''
      });
      setMinOrder(Number(minRes.data?.valor_minimo) || 0);
    } catch (error) {
      console.error('Erro ao carregar:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers de Banner
  const handleSaveBanner = async (data) => {
    if (editingBanner) {
      await axios.put(`${API}/site-banners/${editingBanner.id}`, data);
    } else {
      await axios.post(`${API}/site-banners`, data);
    }
    fetchData();
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Excluir este banner?')) return;
    await axios.delete(`${API}/site-banners/${id}`);
    toast.success('Banner excluído');
    fetchData();
  };

  // Handlers de Seção
  const handleSaveSection = async (data) => {
    if (editingSection) {
      await axios.put(`${API}/site-sections/${editingSection.id}`, data);
    } else {
      await axios.post(`${API}/site-sections`, data);
    }
    fetchData();
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Excluir esta seção?')) return;
    await axios.delete(`${API}/site-sections/${id}`);
    toast.success('Seção excluída');
    fetchData();
  };

  // Handlers de Categoria
  const handleSaveCategory = async (data) => {
    if (editingCategory) {
      await axios.put(`${API}/categories/${editingCategory.id}`, data);
    } else {
      await axios.post(`${API}/categories`, data);
    }
    fetchData();
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    await axios.delete(`${API}/categories/${id}`);
    toast.success('Categoria excluída');
    fetchData();
  };

  const handleSyncCategoriesFromProducts = async () => {
    try {
      const resp = await axios.post(`${API}/categories/sync-from-products`);
      const { criadas = 0, atualizadas = 0 } = resp.data || {};
      if (criadas > 0) {
        toast.success(`✅ ${criadas} nova(s) categoria(s) criada(s) a partir dos produtos`);
      } else {
        toast.info(`Tudo sincronizado. ${atualizadas} categoria(s) com contagem atualizada.`);
      }
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao sincronizar categorias');
    }
  };

  // Handler de Configurações do Site
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/site-settings`, siteSettings);
      toast.success('Configuracoes salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configuracoes');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
            <Layout className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-heading font-bold">Gerenciar Expositor</h2>
            <p className="text-muted-foreground mt-1">Banners, seções e categorias do catálogo</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn-secondary flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Ver Site
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-xl">
        {[
          { id: 'banners', label: 'Banners', icon: Image, count: banners.length },
          { id: 'sections', label: 'Secoes', icon: Layers, count: sections.length },
          { id: 'vitrines', label: 'Vitrines', icon: Flame, count: vitrines.length },
          { id: 'categories', label: 'Categorias', icon: FolderOpen, count: categories.length },
          { id: 'layout', label: 'Layout', icon: Layout, count: null },
          { id: 'settings', label: 'Config', icon: Layout, count: null }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`site-editor-tab-${tab.id}`}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-card shadow text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== null && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Banners */}
      {activeTab === 'banners' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Banners aparecem no carrossel principal. Configure timers para ofertas temporárias.
            </p>
            <button
              onClick={() => { setEditingBanner(null); setShowBannerModal(true); }}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Novo Banner
            </button>
          </div>

          {banners.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-xl">
              <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum banner</h3>
              <button
                onClick={() => { setEditingBanner(null); setShowBannerModal(true); }}
                className="btn-primary mt-4"
              >
                Criar Primeiro Banner
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              <p className="text-xs text-muted-foreground italic">💡 Arraste pelo ícone <GripVertical className="w-3 h-3 inline" /> para reordenar.</p>
              {banners.map((banner, index) => (
                <div
                  key={banner.id}
                  className={`bg-card border border-border rounded-xl overflow-hidden flex transition-all ${
                    dragItem?.kind === 'banner' && dragItem.index === index ? 'opacity-40' : ''
                  } ${dragOverIndex === index && dragItem?.kind === 'banner' ? 'ring-2 ring-primary' : ''}`}
                  onDragOver={handleDragOver('banner', index)}
                  onDrop={handleDrop('banner', index)}
                  onDragEnd={handleDragEnd}
                  data-testid={`banner-card-${banner.id}`}
                >
                  <div
                    draggable
                    onDragStart={handleDragStart('banner', index, banner.id)}
                    className="px-2 flex items-center justify-center text-muted-foreground cursor-grab active:cursor-grabbing hover:bg-accent border-r border-border"
                    title="Arraste para reordenar"
                    data-testid={`banner-drag-handle-${banner.id}`}
                  >
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="w-64 h-36 bg-muted flex-shrink-0">
                    {banner.image_url ? (
                      <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Image className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {banner.active ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                            <Check className="w-3 h-3" /> Ativo
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            <EyeOff className="w-3 h-3" /> Inativo
                          </span>
                        )}
                        {(banner.start_date || banner.end_date) && (
                          <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" /> Timer
                          </span>
                        )}
                        {banner.link_type && banner.link_type !== 'none' && (
                          <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                            <Link className="w-3 h-3" /> {banner.link_type}
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold">{banner.title || 'Sem título'}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-1">{banner.description || banner.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditingBanner(banner); setShowBannerModal(true); }}
                        className="p-2 hover:bg-accent rounded-lg text-primary"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteBanner(banner.id)}
                        className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Seções */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Seções de destaque aparecem abaixo do banner principal
            </p>
            <button
              onClick={() => { setEditingSection(null); setShowSectionModal(true); }}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nova Seção
            </button>
          </div>

          {sections.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-xl">
              <Layers className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma seção</h3>
              <button
                onClick={() => { setEditingSection(null); setShowSectionModal(true); }}
                className="btn-primary mt-4"
              >
                Criar Primeira Seção
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  className={`bg-card border border-border rounded-xl overflow-hidden group transition-all ${
                    dragItem?.kind === 'section' && dragItem.index === index ? 'opacity-40' : ''
                  } ${dragOverIndex === index && dragItem?.kind === 'section' ? 'ring-2 ring-primary' : ''}`}
                  onDragOver={handleDragOver('section', index)}
                  onDrop={handleDrop('section', index)}
                  onDragEnd={handleDragEnd}
                  data-testid={`section-card-${section.id}`}
                >
                  <div className="relative aspect-[3/4] bg-muted">
                    <div
                      draggable
                      onDragStart={handleDragStart('section', index, section.id)}
                      className="absolute top-2 left-2 z-10 p-1 bg-black/60 text-white rounded cursor-grab active:cursor-grabbing"
                      title="Arraste para reordenar"
                      data-testid={`section-drag-handle-${section.id}`}
                    >
                      <GripVertical className="w-4 h-4" />
                    </div>
                    {section.image_url ? (
                      <img src={section.image_url} alt={section.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Layers className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 text-white">
                      <p className="text-xs opacity-80">{section.subtitle}</p>
                      <p className="font-bold">{section.title}</p>
                    </div>
                    {!section.active && (
                      <div className="absolute top-2 right-2 bg-gray-900/80 text-white text-xs px-2 py-1 rounded">
                        Inativo
                      </div>
                    )}
                    {(section.start_date || section.end_date) && (
                      <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate">→ {section.category}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditingSection(section); setShowSectionModal(true); }}
                        className="p-1.5 hover:bg-accent rounded text-primary"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="p-1.5 hover:bg-red-100 rounded text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vitrines Promocionais */}
      {activeTab === 'vitrines' && (
        <VitrinesTab
          vitrines={vitrines}
          products={products}
          reload={fetchData}
          onEdit={(v) => { setEditingVitrine(v); setShowVitrineModal(true); }}
          onNew={() => { setEditingVitrine(null); setShowVitrineModal(true); }}
        />
      )}

      {/* Categorias */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Gerencie as categorias de produtos do catálogo. Categorias atribuídas a produtos são criadas <b>automaticamente</b> aqui.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncCategoriesFromProducts}
                className="px-3 py-2 rounded-lg border border-border hover:bg-accent text-sm font-medium flex items-center gap-2"
                title="Cria automaticamente nas Coleções toda categoria já usada em algum produto"
                data-testid="btn-sync-categorias"
              >
                <RefreshCw className="w-4 h-4" />
                Sincronizar dos produtos
              </button>
              <button
                onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nova Categoria
              </button>
            </div>
          </div>

          <div className="grid gap-3">
            <p className="text-xs text-muted-foreground italic">💡 Arraste pelo ícone <GripVertical className="w-3 h-3 inline" /> para reordenar as categorias no catálogo.</p>
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`bg-card border border-border rounded-xl p-4 flex items-center gap-4 transition-all ${
                  dragItem?.kind === 'category' && dragItem.index === index ? 'opacity-40' : ''
                } ${dragOverIndex === index && dragItem?.kind === 'category' ? 'ring-2 ring-primary' : ''}`}
                onDragOver={handleDragOver('category', index)}
                onDrop={handleDrop('category', index)}
                onDragEnd={handleDragEnd}
                data-testid={`category-card-${category.id}`}
              >
                <div
                  draggable
                  onDragStart={handleDragStart('category', index, category.id)}
                  className="text-muted-foreground cursor-grab active:cursor-grabbing p-1 hover:bg-accent rounded"
                  title="Arraste para reordenar"
                  data-testid={`category-drag-handle-${category.id}`}
                >
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="w-16 h-16 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                  {category.image_url ? (
                    <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{category.name}</h4>
                    {category.active === false && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inativa</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{category.description || 'Sem descrição'}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {category.product_count || 0} produtos • Ordem: {category.order}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditingCategory(category); setShowCategoryModal(true); }}
                    className="p-2 hover:bg-accent rounded-lg text-primary"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Layout do Site */}
      {activeTab === 'layout' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Layout className="w-5 h-5 text-primary" />
              Organizar Layout do Site
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Arraste para reordenar, ative/desative seções. A ordem aqui reflete no catálogo público.
            </p>
            <div className="space-y-2">
              {(siteSettings.layout_sections || [])
                .sort((a, b) => a.order - b.order)
                .map((section, idx) => (
                <div key={section.id} className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg group hover:border-primary/50 transition-all"
                  data-testid={`layout-section-${section.id}`}>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => {
                        if (idx === 0) return;
                        const sorted = [...(siteSettings.layout_sections || [])].sort((a, b) => a.order - b.order);
                        const temp = sorted[idx].order;
                        sorted[idx].order = sorted[idx - 1].order;
                        sorted[idx - 1].order = temp;
                        setSiteSettings(prev => ({ ...prev, layout_sections: sorted }));
                      }}
                      disabled={idx === 0}
                      className="p-1 hover:bg-accent rounded disabled:opacity-20">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        const sorted = [...(siteSettings.layout_sections || [])].sort((a, b) => a.order - b.order);
                        if (idx >= sorted.length - 1) return;
                        const temp = sorted[idx].order;
                        sorted[idx].order = sorted[idx + 1].order;
                        sorted[idx + 1].order = temp;
                        setSiteSettings(prev => ({ ...prev, layout_sections: sorted }));
                      }}
                      disabled={idx >= (siteSettings.layout_sections || []).length - 1}
                      className="p-1 hover:bg-accent rounded disabled:opacity-20">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{section.name}</p>
                    <p className="text-xs text-muted-foreground">{section.id}</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-muted-foreground">{section.enabled !== false ? 'Ativo' : 'Desativado'}</span>
                    <div className={`relative w-10 h-5 rounded-full transition-colors ${section.enabled !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                      onClick={() => {
                        const updated = (siteSettings.layout_sections || []).map(s =>
                          s.id === section.id ? { ...s, enabled: !(s.enabled !== false) } : s
                        );
                        setSiteSettings(prev => ({ ...prev, layout_sections: updated }));
                      }}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${section.enabled !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Preview da ordem:</strong>{' '}
                {(siteSettings.layout_sections || [])
                  .sort((a, b) => a.order - b.order)
                  .filter(s => s.enabled !== false)
                  .map(s => s.name)
                  .join(' → ')}
              </p>
            </div>
          </div>
          <button onClick={handleSaveSettings} disabled={savingSettings}
            className="btn-primary flex items-center gap-2" data-testid="save-layout-btn">
            <Save className="w-4 h-4" />
            {savingSettings ? 'Salvando...' : 'Salvar Layout'}
          </button>
        </div>
      )}

      {/* Configurações */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Pedido Mínimo Global */}
          <div className="bg-card border border-border rounded-xl p-6" data-testid="config-pedido-minimo">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Layout className="w-5 h-5 text-amber-500" />
              Pedido Mínimo (Cliente & Vendedor)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Valor mínimo <b>obrigatório</b> para qualquer pedido feito pelo cliente ou via link do vendedor.
              Este valor é <b>global</b> — vendedor e cliente <b>não podem alterar</b>, só você aqui.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium mb-1 text-muted-foreground">Valor mínimo (R$)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-muted-foreground">R$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={minOrder}
                    onChange={(e) => setMinOrder(e.target.value)}
                    placeholder="0,00 (sem mínimo)"
                    className="input-field text-sm flex-1"
                    data-testid="min-order-input"
                  />
                </div>
              </div>
              <button
                onClick={saveMinOrder}
                disabled={savingMinOrder}
                className="btn-primary px-4 py-2 disabled:opacity-50 flex items-center gap-2"
                data-testid="min-order-save-btn"
              >
                {savingMinOrder ? 'Salvando...' : 'Salvar Pedido Mínimo'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground italic mt-3">
              💡 Coloque <b>0</b> para não exigir mínimo. O valor vale igual para todos os clientes e links.
            </p>
          </div>

          {/* Personalizar Cores do Painel Admin */}
          <div data-testid="config-admin-theme">
            <ThemeColorsCard
              endpoint="/admin/theme-colors"
              initialColors={siteSettings?.admin_theme_colors || {}}
              testidPrefix="admin-theme"
            />
          </div>

          {/* Banner do Vendedor */}
          <div className="bg-card border border-border rounded-xl p-6" data-testid="config-vendedor-banner">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-500" />
              Banner do Vendedor
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Imagem exibida no topo do dashboard do vendedor. Deixe vazio para não mostrar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Imagem (URL ou upload)</label>
                <input
                  type="text"
                  value={siteSettings.vendedor_banner_url || ''}
                  onChange={(e) => setSiteSettings({ ...siteSettings, vendedor_banner_url: e.target.value })}
                  placeholder="https://... ou data:image/..."
                  className="input-field w-full text-sm"
                  data-testid="vendedor-banner-url"
                />
                <input
                  type="file" accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    if (f.size > 3 * 1024 * 1024) { toast.error('Imagem muito grande (máx 3MB)'); return; }
                    const rd = new FileReader();
                    rd.onloadend = () => setSiteSettings({ ...siteSettings, vendedor_banner_url: rd.result });
                    rd.readAsDataURL(f);
                  }}
                  className="mt-2 text-xs"
                  data-testid="vendedor-banner-upload"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Link ao clicar (opcional)</label>
                <input
                  type="text"
                  value={siteSettings.vendedor_banner_link || ''}
                  onChange={(e) => setSiteSettings({ ...siteSettings, vendedor_banner_link: e.target.value })}
                  placeholder="https://..."
                  className="input-field w-full text-sm"
                  data-testid="vendedor-banner-link"
                />
              </div>
            </div>
            {siteSettings.vendedor_banner_url && (
              <div className="mt-3 rounded-lg overflow-hidden border border-border">
                <img src={siteSettings.vendedor_banner_url} alt="Preview" className="w-full h-32 object-cover" />
              </div>
            )}
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/site-settings`, {
                    vendedor_banner_url: siteSettings.vendedor_banner_url || '',
                    vendedor_banner_link: siteSettings.vendedor_banner_link || '',
                  });
                  toast.success('Banner do vendedor atualizado!');
                } catch { toast.error('Erro ao salvar banner'); }
              }}
              className="btn-primary px-4 py-2 mt-3 text-sm"
              data-testid="vendedor-banner-save"
            >
              Salvar Banner do Vendedor
            </button>
          </div>

          {/* Área do Cliente */}
          <div className="bg-card border border-border rounded-xl p-6" data-testid="config-cliente-area">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Layout className="w-5 h-5 text-emerald-500" />
              Portal do Cliente — Destaques
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Controle o que aparece no dashboard do cliente assim que ele logar.
            </p>
            <div className="space-y-2">
              {[
                { key: 'cliente_show_banner', label: 'Mostrar banner rotativo do site', defaultOn: true },
                { key: 'cliente_show_promos', label: 'Mostrar produtos em promoção (Ofertas Especiais)', defaultOn: false },
                { key: 'cliente_show_recents', label: 'Mostrar produtos adicionados recentemente (Novidades)', defaultOn: true },
              ].map(({ key, label, defaultOn }) => (
                <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer">
                  <input
                    type="checkbox"
                    checked={siteSettings[key] !== undefined ? !!siteSettings[key] : !!defaultOn}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-4 h-4"
                    data-testid={`config-${key}`}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
              <p className="text-xs text-muted-foreground italic pt-1">
                💡 Para definir <b>quais produtos</b> aparecem em promoção, vá em <b>Produtos</b> e clique no ícone <Flame className="w-3 h-3 inline text-orange-500" /> ao lado de cada produto.
              </p>
            </div>
          </div>

          {/* Letreiro Marquee */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Layout className="w-5 h-5 text-primary" />
              Letreiro Superior (Marquee)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Textos que passam no topo do site como um letreiro. Pode usar emojis!
            </p>
            <div className="space-y-3">
              {[0, 1, 2].map(i => (
                <div key={i}>
                  <label className="block text-xs font-medium mb-1">Texto {i + 1}</label>
                  <input
                    type="text"
                    value={(siteSettings.marquee_texts || [])[i] || ''}
                    onChange={(e) => {
                      const texts = [...(siteSettings.marquee_texts || ['', '', ''])];
                      texts[i] = e.target.value;
                      setSiteSettings(prev => ({ ...prev, marquee_texts: texts }));
                    }}
                    placeholder={`Ex: Frete Grátis acima de R$ 500 ${i === 0 ? '🚚' : i === 1 ? '💰' : '⭐'}`}
                    className="w-full p-2 border border-border rounded-lg bg-background text-sm"
                    data-testid={`marquee-text-${i}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Cor do Fundo</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={siteSettings.marquee_bg_color || '#ea580c'}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_bg_color: e.target.value }))}
                    className="w-10 h-10 rounded border cursor-pointer" />
                  <input type="text" value={siteSettings.marquee_bg_color || '#ea580c'}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_bg_color: e.target.value }))}
                    className="flex-1 p-2 border border-border rounded text-sm bg-background" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Cor do Texto</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={siteSettings.marquee_text_color || '#ffffff'}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_text_color: e.target.value }))}
                    className="w-10 h-10 rounded border cursor-pointer" />
                  <input type="text" value={siteSettings.marquee_text_color || '#ffffff'}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_text_color: e.target.value }))}
                    className="flex-1 p-2 border border-border rounded text-sm bg-background" />
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Velocidade</label>
                <select value={siteSettings.marquee_speed || 'normal'}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_speed: e.target.value }))}
                  className="w-full p-2 border border-border rounded-lg bg-background text-sm">
                  <option value="slow">Lento</option>
                  <option value="normal">Normal</option>
                  <option value="fast">Rapido</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={siteSettings.marquee_glow || false}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_glow: e.target.checked }))}
                    className="w-4 h-4" />
                  <span className="text-sm">Brilho/Luz</span>
                </label>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={siteSettings.marquee_blink || false}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, marquee_blink: e.target.checked }))}
                    className="w-4 h-4" />
                  <span className="text-sm">Piscar</span>
                </label>
              </div>
            </div>
            {/* Preview */}
            <div className="mt-4 rounded-lg overflow-hidden">
              <div className="py-2 text-center text-sm font-medium"
                style={{ backgroundColor: siteSettings.marquee_bg_color || '#ea580c', color: siteSettings.marquee_text_color || '#ffffff',
                  textShadow: siteSettings.marquee_glow ? `0 0 10px ${siteSettings.marquee_text_color || '#fff'}` : 'none' }}>
                {(siteSettings.marquee_texts || []).filter(t => t).join('  ★  ') || '(Vazio - letreiro nao aparecera)'}
              </div>
            </div>
          </div>

          {/* Banner do Pedido */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Banner da Pagina de Pedido</h3>
            <p className="text-sm text-muted-foreground mb-3">Imagem, GIF ou video que aparece abaixo do header na pagina de pedido do vendedor/cliente.</p>
            <ImageUploader
              value={siteSettings.pedido_banner_url || ''}
              onChange={(url) => setSiteSettings(prev => ({ ...prev, pedido_banner_url: url }))}
              folder="banners"
            />
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={siteSettings.pedido_banner_rounded !== false}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, pedido_banner_rounded: e.target.checked }))}
                className="w-4 h-4" />
              <span className="text-sm">Bordas arredondadas</span>
            </label>
          </div>

          {/* WhatsApp */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">WhatsApp do Catalogo</h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium">Numero do WhatsApp</label>
              <input type="text" value={siteSettings.whatsapp_number}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                placeholder="11999999999" className="w-full p-3 border border-border rounded-lg bg-background text-sm"
                data-testid="whatsapp-number-input" />
            </div>
          </div>

          {/* Endereço da loja + Google Maps */}
          <div className="bg-card border border-border rounded-xl p-6" data-testid="store-location-editor">
            <h3 className="text-lg font-semibold mb-1">📍 Endereço da Loja + Google Maps</h3>
            <p className="text-xs text-muted-foreground mb-3">
              O endereço aparece no rodapé do catálogo junto com um mapa interativo. Para o mapa, copie a URL do iframe (Google Maps → Compartilhar → Incorporar um mapa → copie o atributo src=&quot;...&quot;).
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">CNPJ (aparece no rodapé)</label>
                <input type="text" value={siteSettings.store_cnpj || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, store_cnpj: e.target.value }))}
                  placeholder="00.000.000/0001-00"
                  maxLength={20}
                  className="w-full p-3 border border-border rounded-lg bg-background text-sm"
                  data-testid="store-cnpj-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nome Fantasia (aparece no rodapé)</label>
                <input type="text" value={siteSettings.store_nome_fantasia || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, store_nome_fantasia: e.target.value }))}
                  placeholder="Ex.: Produtos Santa Ana Nacionais e Importados"
                  maxLength={120}
                  className="w-full p-3 border border-border rounded-lg bg-background text-sm"
                  data-testid="store-nome-fantasia-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">E-mail de contato (aparece no rodapé)</label>
                <input type="email" value={siteSettings.store_email || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, store_email: e.target.value }))}
                  placeholder="vendas@empresa.com.br"
                  maxLength={120}
                  className="w-full p-3 border border-border rounded-lg bg-background text-sm"
                  data-testid="store-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Endereço (texto)</label>
                <textarea rows={2} value={siteSettings.store_address || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, store_address: e.target.value }))}
                  placeholder="Rua das Embalagens, 123 — Bairro, Cidade — SP"
                  className="w-full p-3 border border-border rounded-lg bg-background text-sm"
                  data-testid="store-address-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">URL do Google Maps (embed)</label>
                <input type="text" value={siteSettings.store_maps_url || ''}
                  onChange={(e) => setSiteSettings(prev => ({ ...prev, store_maps_url: e.target.value }))}
                  placeholder='https://www.google.com/maps/embed?pb=...'
                  className="w-full p-3 border border-border rounded-lg bg-background text-sm font-mono text-xs"
                  data-testid="store-maps-url-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Cole a URL do <code>src</code> do iframe que o Google Maps gera ao clicar em &quot;Compartilhar&quot; → &quot;Incorporar um mapa&quot;.</p>
              </div>
            </div>
          </div>

          <button onClick={handleSaveSettings} disabled={savingSettings}
            className="btn-primary flex items-center gap-2" data-testid="save-settings-btn">
            <Save className="w-4 h-4" />
            {savingSettings ? 'Salvando...' : 'Salvar Configuracoes'}
          </button>
        </div>
      )}

      {/* Modais */}
      {showBannerModal && (
        <BannerModal
          banner={editingBanner}
          categories={categories}
          products={products}
          onSave={handleSaveBanner}
          onClose={() => { setShowBannerModal(false); setEditingBanner(null); }}
        />
      )}

      {showSectionModal && (
        <SectionModal
          section={editingSection}
          categories={categories}
          onSave={handleSaveSection}
          onClose={() => { setShowSectionModal(false); setEditingSection(null); }}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
        />
      )}

      {showVitrineModal && (
        <VitrineModal
          vitrine={editingVitrine}
          products={products}
          onSave={async () => { setShowVitrineModal(false); setEditingVitrine(null); await fetchData(); }}
          onClose={() => { setShowVitrineModal(false); setEditingVitrine(null); }}
        />
      )}
    </div>
  );
};

// =====================================================
// Aba Vitrines Promocionais
// =====================================================
const VitrinesTab = ({ vitrines, reload, onEdit, onNew }) => {
  const handleDelete = async (v) => {
    if (!window.confirm(`Deletar a vitrine "${v.titles?.catalogo || v.title || 'sem título'}"?`)) return;
    try {
      await axios.delete(`${API}/vitrines/${v.id}`);
      toast.success('Vitrine deletada');
      reload();
    } catch (e) { toast.error('Erro ao deletar'); }
  };
  const toggleActive = async (v) => {
    try {
      await axios.put(`${API}/vitrines/${v.id}`, { active: !v.active });
      reload();
    } catch (e) { toast.error('Erro ao atualizar'); }
  };
  return (
    <div className="space-y-4" data-testid="vitrines-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Vitrines promocionais aparecem no catálogo público, portal do cliente e portal do vendedor. Cada uma pode ter título diferente por portal e timer de oferta.
        </p>
        <button onClick={onNew} className="btn-primary flex items-center gap-2" data-testid="new-vitrine-btn">
          <Plus className="w-4 h-4" /> Nova Vitrine
        </button>
      </div>
      {vitrines.length === 0 ? (
        <div className="bg-card rounded-xl p-8 text-center border border-dashed">
          <Flame className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Nenhuma vitrine cadastrada.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {vitrines.map((v) => {
            const title = v.titles?.catalogo || v.title || 'Sem título';
            const productsCount = v.selection_mode === 'manual' ? (v.product_ids?.length || 0) : `auto: ${v.auto_limit || 8}`;
            return (
              <div key={v.id} className="bg-card rounded-xl p-4 border flex items-center gap-4" data-testid={`vitrine-row-${v.id}`}>
                <div
                  className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: v.header_bg_color || '#1e3a8a' }}
                >
                  <Flame className="w-6 h-6" style={{ color: v.header_text_color || '#fff' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold truncate">{title}</h4>
                    {v.timer_enabled && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">⏱ TIMER</span>}
                    {!v.active && <span className="text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-bold">OCULTA</span>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{v.subtitle || '—'}</p>
                  <p className="text-xs mt-1">
                    <span className="text-muted-foreground">Produtos: </span>
                    <b>{productsCount}</b>
                    <span className="text-muted-foreground ml-3">Exibir em: </span>
                    <b>{Object.entries(v.show_in || {}).filter(([, on]) => on).map(([p]) => p).join(', ') || 'nenhum'}</b>
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(v)} className="p-2 hover:bg-muted rounded" title={v.active ? 'Ocultar' : 'Mostrar'} data-testid={`toggle-vitrine-${v.id}`}>
                    {v.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  <button onClick={() => onEdit(v)} className="p-2 hover:bg-muted rounded" title="Editar" data-testid={`edit-vitrine-${v.id}`}>
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(v)} className="p-2 hover:bg-red-100 rounded text-red-600" title="Deletar" data-testid={`delete-vitrine-${v.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =====================================================
// Modal: Criar/Editar Vitrine Promocional
// =====================================================
const VitrineModal = ({ vitrine, products, onSave, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titles: {
      catalogo: vitrine?.titles?.catalogo || '🔥 MAIS VENDIDOS',
      cliente: vitrine?.titles?.cliente || '🔥 MAIS VENDIDOS',
      vendedor: vitrine?.titles?.vendedor || '🔥 MAIS VENDIDOS PARA VENDER',
    },
    subtitle: vitrine?.subtitle || 'Os produtos preferidos dos nossos clientes com preços especiais!',
    header_bg_color: vitrine?.header_bg_color || '#1e3a8a',
    header_text_color: vitrine?.header_text_color || '#ffffff',
    timer_enabled: !!vitrine?.timer_enabled,
    timer_end: vitrine?.timer_end || '',
    timer_label: vitrine?.timer_label || 'OFERTA POR TEMPO LIMITADO!',
    selection_mode: vitrine?.selection_mode || 'auto_bestsellers',
    product_ids: vitrine?.product_ids || [],
    auto_limit: vitrine?.auto_limit || 8,
    show_in: {
      catalogo: vitrine?.show_in?.catalogo !== false,
      cliente: vitrine?.show_in?.cliente !== false,
      vendedor: vitrine?.show_in?.vendedor !== false,
    },
    discount_badge: vitrine?.discount_badge !== false,
    active: vitrine?.active !== false,
  });

  const toggleProduct = (id) => {
    setForm(f => ({
      ...f,
      product_ids: f.product_ids.includes(id)
        ? f.product_ids.filter(x => x !== id)
        : [...f.product_ids, id],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (vitrine?.id) {
        await axios.put(`${API}/vitrines/${vitrine.id}`, form);
      } else {
        await axios.post(`${API}/vitrines`, form);
      }
      toast.success('Vitrine salva');
      onSave();
    } catch (e) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="vitrine-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-bold flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" /> {vitrine ? 'Editar' : 'Nova'} Vitrine Promocional</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5">
          {/* Títulos por portal */}
          <div>
            <label className="text-sm font-bold mb-2 block">Títulos (um por portal)</label>
            <div className="space-y-2">
              {['catalogo', 'cliente', 'vendedor'].map(portal => (
                <div key={portal} className="flex items-center gap-2">
                  <span className="text-xs w-20 text-muted-foreground uppercase font-bold">{portal}</span>
                  <input
                    type="text"
                    value={form.titles[portal]}
                    onChange={e => setForm(f => ({ ...f, titles: { ...f.titles, [portal]: e.target.value } }))}
                    className="flex-1 px-3 py-2 border rounded-lg bg-transparent text-sm"
                    placeholder={`Título para ${portal}`}
                    data-testid={`vitrine-title-${portal}`}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Subtítulo */}
          <div>
            <label className="text-sm font-bold mb-2 block">Subtítulo</label>
            <input
              type="text"
              value={form.subtitle}
              onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-transparent text-sm"
              data-testid="vitrine-subtitle"
            />
          </div>
          {/* Cores */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold mb-2 block">Cor de fundo do header</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.header_bg_color} onChange={e => setForm(f => ({ ...f, header_bg_color: e.target.value }))} className="w-12 h-10 rounded border" data-testid="vitrine-bg-color" />
                <input type="text" value={form.header_bg_color} onChange={e => setForm(f => ({ ...f, header_bg_color: e.target.value }))} className="flex-1 px-2 py-2 border rounded-lg bg-transparent text-sm" />
              </div>
            </div>
            <div>
              <label className="text-sm font-bold mb-2 block">Cor do texto do header</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.header_text_color} onChange={e => setForm(f => ({ ...f, header_text_color: e.target.value }))} className="w-12 h-10 rounded border" />
                <input type="text" value={form.header_text_color} onChange={e => setForm(f => ({ ...f, header_text_color: e.target.value }))} className="flex-1 px-2 py-2 border rounded-lg bg-transparent text-sm" />
              </div>
            </div>
          </div>
          {/* Timer */}
          <div className="border rounded-lg p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.timer_enabled} onChange={e => setForm(f => ({ ...f, timer_enabled: e.target.checked }))} data-testid="vitrine-timer-toggle" />
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-bold">Ativar contagem regressiva (OFERTA POR TEMPO LIMITADO)</span>
            </label>
            {form.timer_enabled && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Termina em</label>
                  <input
                    type="datetime-local"
                    value={form.timer_end ? form.timer_end.slice(0, 16) : ''}
                    onChange={e => setForm(f => ({ ...f, timer_end: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                    className="w-full px-3 py-2 border rounded-lg bg-transparent text-sm"
                    data-testid="vitrine-timer-end"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Rótulo do timer</label>
                  <input
                    type="text"
                    value={form.timer_label}
                    onChange={e => setForm(f => ({ ...f, timer_label: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg bg-transparent text-sm"
                  />
                </div>
              </div>
            )}
          </div>
          {/* Seleção de produtos */}
          <div className="border rounded-lg p-4 space-y-3">
            <label className="text-sm font-bold block">Produtos da vitrine</label>
            <div className="flex gap-2">
              {['auto_bestsellers', 'manual'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setForm(f => ({ ...f, selection_mode: mode }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${form.selection_mode === mode ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  data-testid={`vitrine-mode-${mode}`}
                >
                  {mode === 'auto_bestsellers' ? '🤖 Automático (mais vendidos)' : '✋ Manual (selecionar)'}
                </button>
              ))}
            </div>
            {form.selection_mode === 'auto_bestsellers' ? (
              <div>
                <label className="text-xs text-muted-foreground">Quantos produtos mostrar?</label>
                <input
                  type="number" min="1" max="20"
                  value={form.auto_limit}
                  onChange={e => setForm(f => ({ ...f, auto_limit: Math.max(1, parseInt(e.target.value, 10) || 8) }))}
                  className="mt-1 w-24 px-3 py-2 border rounded-lg bg-transparent text-sm"
                  data-testid="vitrine-auto-limit"
                />
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
                {products.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground">Nenhum produto cadastrado.</div>
                ) : products.map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.product_ids.includes(p.id)}
                      onChange={() => toggleProduct(p.id)}
                      data-testid={`vitrine-product-${p.id}`}
                    />
                    {p.fotos?.[0] ? (
                      <img src={p.fotos[0]} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : <div className="w-8 h-8 rounded bg-muted" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{p.produto}</p>
                      <p className="text-[10px] text-muted-foreground">{p.categoria} · {p.codigo}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          {/* Visibilidade por portal */}
          <div>
            <label className="text-sm font-bold mb-2 block">Exibir em quais portais?</label>
            <div className="flex gap-4">
              {['catalogo', 'cliente', 'vendedor'].map(portal => (
                <label key={portal} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.show_in[portal]}
                    onChange={e => setForm(f => ({ ...f, show_in: { ...f.show_in, [portal]: e.target.checked } }))}
                    data-testid={`vitrine-show-${portal}`}
                  />
                  <span className="text-sm capitalize">{portal}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Extras */}
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.discount_badge} onChange={e => setForm(f => ({ ...f, discount_badge: e.target.checked }))} />
              <span className="text-sm">Mostrar badge de desconto (%) quando houver `valor_caixa_original`</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="vitrine-active" />
              <span className="text-sm">Vitrine ativa</span>
            </label>
          </div>
        </div>
        <div className="p-5 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-muted text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary px-4 py-2 rounded-lg flex items-center gap-2 text-sm" data-testid="save-vitrine">
            <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar Vitrine'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Exportar os componentes de upload para uso em outras páginas
export { ImageUploader, MultiImageUploader, VideoUploader };

export default SiteEditor;
